import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PageCardWrapper, PageContent, PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {UserPersonalDataForm} from '@/features/users/components/user-personal-data-form/UserPersonalDataForm'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useTranslation} from '@/translations/i18n'
import {UserResidenceForm} from '@/features/users/components/user-residence-form/UserResidenceForm'
import {UserDomicileForm} from '@/features/users/components/user-domicile-form/UserDomicileForm'
import {calculateTrienniumPeriod} from '@/features/users/utils'
import {UserJobInfoForm} from '@/features/users/components/user-job-info-form/UserJobInfoForm'
import {UserContractualDataForm} from '@/features/users/components/user-contractual-data-form/UserContractualDataForm'
import {UserEducationForm} from '@/features/users/components/user-education-form/UserEducationForm'
import {Helmet} from 'react-helmet'
import {UserAccessProfileForm} from '@/features/users/components/user-access-profile-form/UserAccessProfileForm'
import {UserPrivacyForm} from '@/features/users/components/user-privacy-form/UserPrivacyForm'
import {useRef, useState} from 'react'
import {CreateUserConfirmationModal} from '@/features/users/components/create-user-confirmation-modal/CreateUserConfirmationModal'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {UnsavedBlocker} from '@/components/commons/unsaved-blocker/UnsavedBlocker'
import {
    FormGridSection,
    FormGridSectionItem,
    FormGridSectionTitle,
    FormGridTitle
} from '@/components/ui/form-atoms/FormAtoms'
import {UserContactsForm} from '@/features/users/components/user-contacts-form/UserContactsForm'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {UserFormSchema} from '@/features/users/types'
import {useDialogStore} from '@ariakit/react'
import {AxiosError} from 'axios'
import {MUTATION_KEYS} from '@/queryClient'
import {useMutationState} from '@tanstack/react-query'
import {usePermissions} from '@/features/auth/store/store'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'

export const CreateUser = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isErrorBannerVisible, setIsBannerVisible] = useState(false)
    const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
    const errorBannerRef = useRef<HTMLDivElement>(null)
    const viewportRef = useRef<HTMLDivElement>(null)

    const confirmDialogStore = useDialogStore({
        open: isConfirmationModalVisible,
        setOpen: setIsConfirmationModalVisible
    })

    // methods
    const form = useForm<UserFormSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserFormSchema),
        defaultValues: {
            isBornAbroad: false,
            isDifferentDomicile: false,
            raExempt: false,
            ecmScore: {
                period: calculateTrienniumPeriod(new Date()),
                score: undefined,
                cogeapsCertification: undefined
            },
            privacyB: false,
            privacyC: false,
            fm68: false
        }
    })

    const onErrorConfirmationModal = (errors: AxiosError<{errors: {field: string; rule: string}[]}>) => {
        setIsConfirmationModalVisible(false)
        if (errors?.response?.data.errors) {
            setIsBannerVisible(true)
            errors.response.data.errors.forEach(error => {
                if (error.field == 'email') {
                    form.setError('email', {
                        message: t('errors:email_already_exists'),
                        type: 'custom'
                    })
                }

                if (error.field == 'taxCode' && error.rule == 'regex') {
                    form.setError('taxCode', {
                        message: t('errors:invalid_tax_code'),
                        type: 'custom'
                    })
                }

                if (error.field == 'taxCode' && error.rule == 'database.exists') {
                    form.setError('taxCode', {
                        message: t('errors:tax_code_already_exists'),
                        type: 'custom'
                    })
                }

                if (error.field == 'iban') {
                    form.setError('iban', {
                        message: t('errors:iban'),
                        type: 'custom'
                    })
                }

                if (error.field == 'phoneNumber') {
                    form.setError('phoneNumber', {
                        message: t('errors:phone_number'),
                        type: 'custom'
                    })
                }
            })

            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }
    }

    // actions
    const handleSubmit = form.handleSubmit(
        () => confirmDialogStore.setOpen(true),
        errors => {
            setIsBannerVisible(true)
            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
            console.error(errors)
        }
    )

    // form observer
    const status = useWatch({control: form.control, name: 'status'})
    const specialization = useWatch({control: form.control, name: 'specialization'})
    const contractType = useWatch({control: form.control, name: 'contractType'})

    // Track all create patient mutation states and extract the status of the most recent mutation
    // This is used to determine if there are unsaved changes that should block navigation
    const createUserMutationStatuses = useMutationState({
        filters: {
            mutationKey: [MUTATION_KEYS.CREATE_USER]
        },
        select: mutation => mutation.state.status
    })

    const createUserMutationStatus = createUserMutationStatuses[createUserMutationStatuses.length - 1]

    if (!permissions?.users?.write) {
        return <NoPermissionBox />
    }

    return (
        <PageCardWrapper>
            <Helmet title={t('users:create_meta_title')} />
            <PageHeading align="center" justify="space-between">
                <h2>{t('users:add_user_title')}</h2>
                <Flexbox gap={2}>
                    <ButtonLink to={'..'} variant="secondary">
                        {t('commons:cancel')}
                    </ButtonLink>
                    <Button onClick={handleSubmit} variant="primaryNeutral">
                        {t('commons:add')}
                    </Button>
                </Flexbox>
            </PageHeading>
            <Divider direction="horizontal" />
            <PageContent padding={0}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: viewportRef}}>
                    {isErrorBannerVisible && (
                        <>
                            <BannerAlert ref={errorBannerRef} onClose={() => setIsBannerVisible(false)} />
                            <Divider direction="horizontal" />
                        </>
                    )}
                    <UnsavedBlocker
                        isDirty={createUserMutationStatus == 'idle' && !!Object.keys(form.formState.dirtyFields).length}
                    >
                        <FormProvider {...form}>
                            <FormGridSection>
                                <FormGridSectionItem>
                                    <FormGridSectionTitle>{t('commons:personal_data')}</FormGridSectionTitle>
                                </FormGridSectionItem>

                                <FormGridSectionItem>
                                    <Flexbox direction="column" gap={8} fullWidth>
                                        <Flexbox direction="column" gap={2.5} fullWidth>
                                            <FormGridTitle>{t('commons:personal_info')}</FormGridTitle>
                                            <UserPersonalDataForm />
                                        </Flexbox>

                                        <Flexbox direction="column" gap={2.5} fullWidth>
                                            <FormGridTitle>{t('commons:residence')}</FormGridTitle>
                                            <UserResidenceForm />
                                        </Flexbox>

                                        <Flexbox direction="column" gap={2.5} fullWidth>
                                            <FormGridTitle>{t('commons:domicile')}</FormGridTitle>
                                            <UserDomicileForm />
                                        </Flexbox>
                                    </Flexbox>
                                </FormGridSectionItem>
                            </FormGridSection>

                            <Divider direction="horizontal" />

                            <FormGridSection>
                                <FormGridSectionItem>
                                    <FormGridSectionTitle>{t('users:job_data_title')}</FormGridSectionTitle>
                                </FormGridSectionItem>
                                <FormGridSectionItem>
                                    <UserJobInfoForm />
                                </FormGridSectionItem>
                            </FormGridSection>

                            {status == 'operative' && (
                                <>
                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>{t('users:contractual_data')}</FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <UserContractualDataForm />
                                        </FormGridSectionItem>
                                    </FormGridSection>
                                </>
                            )}

                            {specialization && (
                                <>
                                    {specialization?.name != 'Amministrativo' && (
                                        <>
                                            <Divider direction="horizontal" />

                                            <FormGridSection>
                                                <FormGridSectionItem>
                                                    <FormGridSectionTitle>
                                                        {t('users:professional_data')}{' '}
                                                        <span className="description">({t('commons:optional')})</span>
                                                    </FormGridSectionTitle>
                                                </FormGridSectionItem>
                                                <FormGridSectionItem>
                                                    <UserEducationForm />
                                                </FormGridSectionItem>
                                            </FormGridSection>
                                        </>
                                    )}

                                    {status == 'operative' && (
                                        <>
                                            <Divider direction="horizontal" />

                                            <FormGridSection>
                                                <FormGridSectionItem>
                                                    <FormGridSectionTitle>
                                                        {t('users:access_profile')}{' '}
                                                        <span className="description">({t('commons:optional')})</span>
                                                    </FormGridSectionTitle>
                                                </FormGridSectionItem>
                                                <FormGridSectionItem>
                                                    <UserAccessProfileForm />
                                                </FormGridSectionItem>
                                            </FormGridSection>
                                        </>
                                    )}

                                    <Divider direction="horizontal" />

                                    <FormGridSection>
                                        <FormGridSectionItem>
                                            <FormGridSectionTitle>
                                                {t('commons:contacts')}{' '}
                                                <span className="description">({t('commons:optional')})</span>
                                            </FormGridSectionTitle>
                                        </FormGridSectionItem>
                                        <FormGridSectionItem>
                                            <UserContactsForm
                                                title={
                                                    <FormGridSectionTitle>{t('commons:contacts')}</FormGridSectionTitle>
                                                }
                                            />
                                        </FormGridSectionItem>
                                    </FormGridSection>

                                    {contractType && (
                                        <>
                                            <Divider direction="horizontal" />
                                            <FormGridSection>
                                                <FormGridSectionItem>
                                                    <FormGridSectionTitle>
                                                        {t('commons:privacy')}{' '}
                                                        <span className="description">({t('commons:optional')})</span>
                                                    </FormGridSectionTitle>
                                                </FormGridSectionItem>
                                                <FormGridSectionItem>
                                                    <UserPrivacyForm />
                                                </FormGridSectionItem>
                                            </FormGridSection>
                                        </>
                                    )}
                                </>
                            )}

                            {isConfirmationModalVisible && (
                                <CreateUserConfirmationModal
                                    dialogStore={confirmDialogStore}
                                    defaultValues={form.getValues()}
                                    onErrorCallback={onErrorConfirmationModal}
                                />
                            )}
                        </FormProvider>
                    </UnsavedBlocker>
                </ScrollArea>
            </PageContent>
        </PageCardWrapper>
    )
}
